import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { resolveBriefingPlatformIcon } from "../../utils/iconUtils";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUserRole } from "../../hooks/useUserRole";

import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import {
  useDeleteAssociatedCampaignGoalMutation,
  useDeleteAssociatedCampaignTargetMutation,
  useGetAssociatedCampaignQuery,
  useGetBriefingByIdQuery,
  useUpdateAssociatedCampaignStatusMutation,
} from "../../store/apis/briefingApi";
import { getCampaignStatusEditing } from "../../utils/associatedCampaignStatus";
import CampaignStatusBadge from "../../components/briefings/CampaignStatusBadge";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  canEditAssetSection,
  canViewGoalField,
} from "../../utils/briefingPermissions";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import AssociatedCampaignInfoSection from "../../components/briefings/AssociatedCampaignInfoSection";
import AssociatedCampaignGoalSection from "../../components/briefings/AssociatedCampaignGoalSection";
import AssociatedCampaignTargetSection from "../../components/briefings/AssociatedCampaignTargetSection";
import AssociatedCampaignAssetSection from "../../components/briefings/AssociatedCampaignAssetSection";
import AssociatedCampaignSyncSection from "../../components/briefings/AssociatedCampaignSyncSection";
import AssociatedCampaignMarginSection from "../../components/briefings/AssociatedCampaignMarginSection";
import AutomateWithClaudeButton from "../../components/briefings/AutomateWithClaudeButton";
import { InlineEditProvider } from "../../components/briefings/inline/InlineEditContext";
import GlobalEditControls from "../../components/briefings/inline/GlobalEditControls";
import { extractApiErrorMessage } from "../../components/briefings/inline/apiErrors";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import OrbLoader from "../../components/common/OrbLoader";
import { createNumberFormat } from "../../utils/numberFormat";
import { useGoBack } from "../../hooks/useGoBack";

type Role = components["schemas"]["Role"];
type CampaignStatus =
  components["schemas"]["_36_Enums.AssociatedCampaignStatus"];

const allowedViewRoles = ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING"] as const;

/** Small label/value pair in the page header's summary row. */
const HeaderStat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 600, letterSpacing: 0.2 }}
    >
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={700} noWrap>
      {value}
    </Typography>
  </Box>
);

const ViewAssociatedCampaign: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const params = useParams<{
    id: string;
    associatedId: string;
  }>();

  const briefingId = params.id?.replace(/['"]/g, "");
  const associatedId = params.associatedId?.replace(/['"]/g, "");
  const collapsed = useSelector(selectSidebarCollapsed);
  const dispatch = useDispatch();

  const { canEditEverything, isAccount, role, canAccessControlAndMargin } =
    useUserRole();

  const canView = role
    ? allowedViewRoles.includes(role as (typeof allowedViewRoles)[number])
    : false;

  const {
    data,
    isFetching,
    isLoading,
    error,
    refetch: refetchAssociatedCampaign,
  } = useGetAssociatedCampaignQuery(
    {
      briefingId: briefingId ?? "",
      associatedId: associatedId ?? "",
    },
    { skip: !canView || !briefingId || !associatedId }
  );

  const { data: briefingResponse } = useGetBriefingByIdQuery(
    { id: briefingId ?? "" },
    { skip: !briefingId }
  );
  const briefing = briefingResponse?.data ?? undefined;
  const companyName = briefing?.companyName || "";
  const companyId = briefing?.companyId || "";
  const isBeApoloCompany =
    briefing?.companyName?.toLowerCase().includes("beapolo") ?? false;

  const [isDeleteGoalModalOpen, setDeleteGoalModalOpen] = useState(false);
  const [isDeleteTargetModalOpen, setDeleteTargetModalOpen] = useState(false);

  const [deleteGoal, { isLoading: isDeletingGoal }] =
    useDeleteAssociatedCampaignGoalMutation();
  const [deleteTarget, { isLoading: isDeletingTarget }] =
    useDeleteAssociatedCampaignTargetMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateAssociatedCampaignStatusMutation();

  const associatedCampaign = data?.data;
  const goal = associatedCampaign?.goal ?? null;
  const target = associatedCampaign?.target ?? null;
  const asset = associatedCampaign?.asset ?? null;

  // Sections await this after saving, so they leave edit mode with fresh data.
  const reload = useCallback(
    () => refetchAssociatedCampaign(),
    [refetchAssociatedCampaign]
  );

  // Deep-link a la sección de Margin (#margin), p. ej. desde /wip.
  const location = useLocation();
  useEffect(() => {
    if (location.hash !== "#margin") return;
    if (isLoading || !canAccessControlAndMargin) return;
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById("margin");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts < 10) {
        attempts += 1;
        setTimeout(tryScroll, 150);
      }
    };
    tryScroll();
  }, [location.hash, isLoading, canAccessControlAndMargin]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const handleBack = () => {
    goBack(briefingId ? `/briefings/${briefingId}` : "/briefings");
  };

  const runDelete = async (
    action: () => Promise<unknown>,
    success: string,
    failure: string,
    close: () => void
  ): Promise<void> => {
    try {
      await action();
      dispatch(setToastMessage({ message: success, severity: "success" }));
      close();
      refetchAssociatedCampaign();
    } catch (err) {
      dispatch(
        setToastMessage({
          message: extractApiErrorMessage(err) ?? failure,
          severity: "error",
        })
      );
    }
  };

  const handleDeleteGoal = () =>
    briefingId && associatedId
      ? runDelete(
          () => deleteGoal({ briefingId, associatedId }).unwrap(),
          t("briefingsView.goalDeleteSuccess", "Goal deleted successfully"),
          t("briefingsView.goalDeleteError", "Failed to delete goal"),
          () => setDeleteGoalModalOpen(false)
        )
      : undefined;

  const handleDeleteTarget = () =>
    briefingId && associatedId
      ? runDelete(
          () => deleteTarget({ briefingId, associatedId }).unwrap(),
          t(
            "briefingsView.targetDeleteSuccess",
            "Targeting details deleted successfully"
          ),
          t(
            "briefingsView.targetDeleteError",
            "Failed to delete targeting details"
          ),
          () => setDeleteTargetModalOpen(false)
        )
      : undefined;

  const canEditGoal = canEditEverything || isAccount;
  const canEditTarget = canEditEverything || isAccount;
  const canEditAsset =
    canEditEverything || canEditAssetSection(role as Role, briefing?.status);
  const canEditAcInfo = canEditEverything || isAccount;
  const canEditSync = canEditEverything || isAccount;
  // El apartado Margin es visible/usable solo para ADMIN/STRATEGY/TECHNICIAN/PRICING.
  const canViewMargin = canAccessControlAndMargin;
  const canEditSomething =
    canEditGoal ||
    canEditTarget ||
    canEditAsset ||
    canEditAcInfo ||
    canEditSync;

  // Same rules as the briefing table: manual lines by hand, connected ones
  // driven by the backend cron except while paused/draft.
  const statusEditing = associatedCampaign
    ? getCampaignStatusEditing({
        campaign: associatedCampaign,
        briefingStatus: briefing?.status,
        role,
        canEditEverything,
        isEditMode:
          (canEditEverything || isAccount) &&
          briefing?.status !== "FINALIZED" &&
          briefing?.status !== "ARCHIVED",
      })
    : null;

  const handleStatusChange = async (status: CampaignStatus) => {
    if (!briefingId || !associatedId) return;
    try {
      await updateStatus({ briefingId, associatedId, status }).unwrap();
      dispatch(
        setToastMessage({
          message: t("common.savedSuccessfully", "Saved successfully"),
          severity: "success",
        })
      );
    } catch (err) {
      dispatch(
        setToastMessage({
          message:
            extractApiErrorMessage(err) ??
            t("common.errorSaving", "Error saving changes"),
          severity: "error",
        })
      );
    }
  };

  const renderHeader = () => {
    const platformIcon = associatedCampaign
      ? resolveBriefingPlatformIcon(associatedCampaign.platform).iconPath
      : null;
    const campaignType = associatedCampaign?.briefingCampaignType;
    const showInvestment =
      goal?.totalInversion !== undefined &&
      goal?.totalInversion !== null &&
      (role ? canViewGoalField(role as Role, "totalInversion") : false);

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            minWidth={0}
            flex={1}
          >
            {platformIcon && (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <Box
                  component="img"
                  src={platformIcon}
                  alt={associatedCampaign?.platform ?? ""}
                  sx={{ width: 26, height: 26 }}
                />
              </Box>
            )}
            <Box minWidth={0} flex={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.3 }}
              >
                {t(
                  "briefingsView.associatedCampaignDetails",
                  "Associated campaign"
                )}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                noWrap
                title={associatedCampaign?.name}
                sx={{
                  fontFamily: "Montserrat",
                  lineHeight: 1.25,
                  minWidth: 0,
                }}
              >
                {associatedCampaign?.name ??
                  t(
                    "briefingsView.associatedCampaignDetails",
                    "Associated campaign"
                  )}
              </Typography>
              {associatedCampaign && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: 0.75 }}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip size="small" label={associatedCampaign.platform} />
                    {campaignType && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(
                          `briefingsCreate.form.briefingCampaignTypeOptions.${campaignType}`,
                          campaignType
                        )}
                      />
                    )}
                    {goal?.funnel && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={goal.funnel}
                      />
                    )}
                  </Stack>
                  {statusEditing && (
                    <Box sx={{ flexShrink: 0 }}>
                      <CampaignStatusBadge
                        showLabel
                        status={statusEditing.status}
                        onChange={
                          statusEditing.editable
                            ? handleStatusChange
                            : undefined
                        }
                        disabled={statusEditing.editable && isUpdatingStatus}
                        excludedStatuses={statusEditing.excludedStatuses}
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        {goal && (
          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            <HeaderStat
              label={t("briefingsView.period", "Periodo")}
              value={
                goal.startDate && goal.endDate
                  ? `${dayjs(goal.startDate).format("DD/MM/YY")} – ${dayjs(goal.endDate).format("DD/MM/YY")}`
                  : "—"
              }
            />
            <HeaderStat
              label={t("briefingsView.goalForm.objective", "Objective")}
              value={goal.objective ?? "—"}
            />
            <HeaderStat
              label={t("briefingsView.goalForm.country", "Country")}
              value={goal.country ?? "—"}
            />
            {showInvestment && (
              <HeaderStat
                label={t(
                  "briefingsView.goalForm.totalInversion",
                  "Total inversion"
                )}
                value={createNumberFormat({
                  maximumFractionDigits: 2,
                  ...(goal.currency && {
                    style: "currency",
                    currency: goal.currency,
                  }),
                }).format(goal.totalInversion as number)}
              />
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const renderContent = () => {
    // Only the first load shows the loader: refetches after a save keep the
    // sections mounted so their edit state survives.
    if (!associatedCampaign && (isLoading || isFetching)) {
      return (
        <Stack direction="row" justifyContent="center" py={8}>
          <OrbLoader
            size="large"
            label={t("common.loading", { defaultValue: "Cargando…" })}
          />
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          {t(
            "briefingsView.associatedCampaignError",
            "Unable to load associated campaign."
          )}
        </Alert>
      );
    }

    if (!associatedCampaign || !briefingId || !associatedId) {
      return (
        <Alert severity="warning">
          {t(
            "briefingsView.associatedCampaignNotFound",
            "Associated campaign not found."
          )}
        </Alert>
      );
    }

    return (
      <Stack spacing={3}>
        <AssociatedCampaignInfoSection
          briefingId={briefingId}
          associatedId={associatedId}
          associatedCampaign={associatedCampaign}
          briefing={briefing}
          companyId={companyId}
          canEdit={canEditAcInfo}
          onSaved={reload}
        />
        <AssociatedCampaignGoalSection
          briefingId={briefingId}
          associatedId={associatedId}
          goal={goal}
          canEdit={canEditGoal}
          onDelete={
            canEditGoal ? () => setDeleteGoalModalOpen(true) : undefined
          }
          onSaved={reload}
          associatedCampaignName={associatedCampaign.name}
          companyName={companyName}
          campaignName={briefing?.campaignName || ""}
          brandOrProduct={briefing?.brandOrProduct || ""}
          subcampaignName={associatedCampaign.subcampaignName}
          isBeApoloCompany={isBeApoloCompany}
          platformType={associatedCampaign.platform}
          campaignType={associatedCampaign.briefingCampaignType}
          briefingStatus={briefing?.status}
          briefingTotalBudget={briefing?.totalBudget}
        />
        <AssociatedCampaignTargetSection
          briefingId={briefingId}
          associatedId={associatedId}
          companyId={companyId}
          target={target}
          canEdit={canEditTarget}
          onDelete={
            canEditTarget ? () => setDeleteTargetModalOpen(true) : undefined
          }
          onSaved={reload}
        />
        <AssociatedCampaignAssetSection
          briefingId={briefingId}
          associatedId={associatedId}
          asset={asset}
          associatedCampaign={associatedCampaign}
          briefing={briefing}
          canEdit={canEditAsset}
          onSaved={reload}
        />
        <AssociatedCampaignSyncSection
          briefingId={briefingId}
          associatedId={associatedId}
          platform={associatedCampaign.platform ?? ""}
          sync={associatedCampaign.sync ?? null}
          linkedInternalCampaignId={associatedCampaign.internalCampaignId}
          onOpenLinkedCampaign={(parentId, internalId) =>
            navigate(`/internal-campaigns/${parentId}/${internalId}`)
          }
          canEdit={canEditSync}
          onSaved={reload}
        />
        {canViewMargin && (
          <Box id="margin" sx={{ scrollMarginTop: 16 }}>
            <AssociatedCampaignMarginSection
              briefingId={briefingId}
              associatedId={associatedId}
              isEditMode={canViewMargin}
              campaignStartDate={goal?.startDate ?? null}
              campaignEndDate={goal?.endDate ?? null}
              goal={
                goal
                  ? {
                      margin: goal.margin ?? 0,
                      grossUnitCost: goal.grossUnitCost ?? 0,
                      netUnitCost: goal.netUnitCost ?? 0,
                      platformInversion: goal.platformInversion ?? 0,
                      targetVolume: goal.expectedVolume ?? 0,
                      objective: goal.objective ?? "",
                    }
                  : null
              }
            />
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <InlineEditProvider>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
            transition: "margin-left 0.3s ease",
            ...(collapsed
              ? { ml: `calc(${theme.spacing(7)} + 12px)` }
              : { ml: `calc(${theme.spacing(32)} + 12px)` }),
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <PageBreadcrumbs sx={{ mb: 0, pl: 0 }} />

          <PageContainer
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Back button sits on its own row right above the first panel,
                left-aligned with it (negative margins pull it tight against
                the panel and line the icon up with the panel's edge). */}
            <Box sx={{ alignSelf: "flex-start", ml: -0.5, mb: -2 }}>
              <Tooltip title={t("briefingsCreate.back", "Back")}>
                <IconButton
                  size="small"
                  onClick={handleBack}
                  aria-label={t("briefingsCreate.back", "Back")}
                >
                  <KeyboardBackspaceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {renderHeader()}
            {associatedCampaign && canEditSomething && (
              // Zero-height sticky rail: "Edit all" starts on the Claude
              // button row and stays pinned to the top while scrolling.
              <Box
                sx={{
                  position: "sticky",
                  top: 16,
                  zIndex: theme.zIndex.appBar - 1,
                  height: 0,
                  mb: -3,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    height: "fit-content",
                    // Solid buttons so content scrolling underneath doesn't show through.
                    "& .MuiButton-root:not(.MuiButton-contained)": {
                      bgcolor: "background.paper",
                    },
                  }}
                >
                  <GlobalEditControls />
                </Box>
              </Box>
            )}
            {associatedCampaign && (
              <Stack direction="row" justifyContent="center" sx={{ py: 0.75 }}>
                <AutomateWithClaudeButton
                  associatedCampaign={associatedCampaign}
                  briefing={briefing}
                  companyId={companyId}
                />
              </Stack>
            )}
            {renderContent()}
          </PageContainer>
        </Box>

        <ConfirmationModal
          open={isDeleteGoalModalOpen}
          title={t(
            "briefingsView.goalDeleteConfirmTitle",
            "Delete goal confirmation"
          )}
          message={t(
            "briefingsView.goalDeleteConfirmMessage",
            "Are you sure you want to delete this goal? This action cannot be undone."
          )}
          onConfirm={handleDeleteGoal}
          onCancel={() => setDeleteGoalModalOpen(false)}
          isLoading={isDeletingGoal}
        />
        <ConfirmationModal
          open={isDeleteTargetModalOpen}
          title={t(
            "briefingsView.targetDeleteConfirmTitle",
            "Delete target confirmation"
          )}
          message={t(
            "briefingsView.targetDeleteConfirmMessage",
            "Are you sure you want to delete the audience targeting details?"
          )}
          onConfirm={handleDeleteTarget}
          onCancel={() => setDeleteTargetModalOpen(false)}
          isLoading={isDeletingTarget}
        />
      </Box>
    </InlineEditProvider>
  );
};

export default ViewAssociatedCampaign;
